import type { CostEstimateData } from "../types";
import { type CostEstimateResult, compositionPct, formatWon } from "../calc";
import { PercentInput } from "./PercentInput";
import { DeltaBadge } from "./DeltaBadge";
import { STANDARD_RATES, STANDARD_EXPENSE_RATES } from "../standardRates";

interface SummaryTableProps {
  data: CostEstimateData;
  result: CostEstimateResult;
  /** 저장 기준선 기준 계산값 (변동량 표시용) */
  savedResult: CostEstimateResult;
  openCategory: "directMaterial" | "directLabor" | null;
  onOpenCategory: (c: "directMaterial" | "directLabor") => void;
  onChangeIndirectMaterialRate: (rate: number) => void;
  onChangeIndirectLaborRate: (rate: number) => void;
  onChangeExpenseRate: (id: string, rate: number) => void;
  onChangeGeneralAdminRate: (rate: number) => void;
  onChangeProfitRate: (rate: number) => void;
  onChangeVatRate: (rate: number) => void;
  /** 요율 수정됨(저장 전) 여부 */
  dirty: {
    indirectMaterial: boolean;
    indirectLabor: boolean;
    generalAdmin: boolean;
    profit: boolean;
    vat: boolean;
    expense: (id: string) => boolean;
  };
}

const TH = "px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50";
const TD = "px-3 py-2 text-sm text-slate-800";

/** 비목/금액/구성비/비고 4열 헤더 */
function ColumnHeader() {
  return (
    <tr>
      <th className={`${TH} text-left`}>비목</th>
      <th className={`${TH} text-right`}>금액</th>
      <th className={`${TH} text-right`}>구성비</th>
      <th className={`${TH} text-left`}>비고</th>
    </tr>
  );
}

/** 섹션 구분 행 (재료비 / 노무비 / 경비) */
function SectionRow({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={4}
        className="bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700"
      >
        {label}
      </td>
    </tr>
  );
}

export function SummaryTable({
  data,
  result,
  savedResult,
  openCategory,
  onOpenCategory,
  onChangeIndirectMaterialRate,
  onChangeIndirectLaborRate,
  onChangeExpenseRate,
  onChangeGeneralAdminRate,
  onChangeProfitRate,
  onChangeVatRate,
  dirty,
}: SummaryTableProps) {
  return (
    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 lg:grid-cols-2">
      {/* ─── 좌측: 재료비 / 노무비 ─── */}
      <div className="bg-white">
        <table className="w-full table-fixed border-collapse [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200 [&_td:nth-child(3)]:text-right">
          <colgroup>
            <col className="w-[22%]" /> {/* 비목 */}
            <col className="w-[26%]" /> {/* 금액 */}
            <col className="w-[36%]" /> {/* 구성비 */}
            <col className="w-[16%]" /> {/* 비고 */}
          </colgroup>
          <thead>
            <ColumnHeader />
          </thead>
          <tbody>
            <SectionRow label="재료비" />

            {/* 직접재료비 (클릭 → 드릴다운) */}
            <tr
              onClick={() => onOpenCategory("directMaterial")}
              className={`cursor-pointer transition-colors hover:bg-primary/5 ${
                openCategory === "directMaterial" ? "bg-primary/10" : ""
              }`}
            >
              <td className={TD}>직접재료비</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.directMaterial)}
                <DeltaBadge
                  value={result.directMaterial - savedResult.directMaterial}
                />
              </td>
              <td className={TD}>
                {compositionPct(result.directMaterial, result.netCost)}
              </td>
              <td className={TD} />
            </tr>

            {/* 간접재료비 = 직접재료비 × rate */}
            <tr>
              <td className={TD}>간접재료비</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.indirectMaterial)}
                <DeltaBadge
                  value={result.indirectMaterial - savedResult.indirectMaterial}
                />
              </td>
              <td className={TD}>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  직접재료비 ×
                  <PercentInput
                    rate={data.indirectMaterialRate}
                    onChange={onChangeIndirectMaterialRate}
                    dirty={dirty.indirectMaterial}
                    baseline={STANDARD_RATES.indirectMaterial}
                  />
                </span>
              </td>
              <td className={TD} />
            </tr>

            {/* 재료비 소계 */}
            <tr className="bg-slate-50 font-semibold text-primary">
              <td className={`${TD} font-semibold text-primary`}>[소계]</td>
              <td
                className={`${TD} text-right font-semibold tabular-nums text-primary`}
              >
                {formatWon(result.materialSubtotal)}
                <DeltaBadge
                  value={result.materialSubtotal - savedResult.materialSubtotal}
                />
              </td>
              <td className={`${TD} font-semibold text-primary`}>
                {compositionPct(result.materialSubtotal, result.netCost)}
              </td>
              <td className={TD} />
            </tr>

            <SectionRow label="노무비" />

            {/* 직접노무비 (클릭 → 드릴다운) */}
            <tr
              onClick={() => onOpenCategory("directLabor")}
              className={`cursor-pointer transition-colors hover:bg-primary/5 ${
                openCategory === "directLabor" ? "bg-primary/10" : ""
              }`}
            >
              <td className={TD}>직접노무비</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.directLabor)}
                <DeltaBadge
                  value={result.directLabor - savedResult.directLabor}
                />
              </td>
              <td className={TD}>
                {compositionPct(result.directLabor, result.netCost)}
              </td>
              <td className={TD} />
            </tr>

            {/* 간접노무비 = 직접노무비 × rate */}
            <tr>
              <td className={TD}>간접노무비</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.indirectLabor)}
                <DeltaBadge
                  value={result.indirectLabor - savedResult.indirectLabor}
                />
              </td>
              <td className={TD}>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  직접노무비 ×
                  <PercentInput
                    rate={data.indirectLaborRate}
                    onChange={onChangeIndirectLaborRate}
                    dirty={dirty.indirectLabor}
                    baseline={STANDARD_RATES.indirectLabor}
                  />
                </span>
              </td>
              <td className={TD} />
            </tr>

            {/* 노무비 소계 */}
            <tr className="bg-slate-50 font-semibold text-primary">
              <td className={`${TD} font-semibold text-primary`}>[소계]</td>
              <td
                className={`${TD} text-right font-semibold tabular-nums text-primary`}
              >
                {formatWon(result.laborSubtotal)}
                <DeltaBadge
                  value={result.laborSubtotal - savedResult.laborSubtotal}
                />
              </td>
              <td className={`${TD} font-semibold text-primary`}>
                {compositionPct(result.laborSubtotal, result.netCost)}
              </td>
              <td className={TD} />
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── 우측: 경비 ─── */}
      <div className="bg-white">
        <table className="w-full table-fixed border-collapse [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200 [&_td:nth-child(3)]:text-right">
          <colgroup>
            <col className="w-[22%]" /> {/* 비목 */}
            <col className="w-[26%]" /> {/* 금액 */}
            <col className="w-[36%]" /> {/* 구성비 */}
            <col className="w-[16%]" /> {/* 비고 */}
          </colgroup>
          <thead>
            <ColumnHeader />
          </thead>
          <tbody>
            <SectionRow label="경비" />

            {result.expenses.map((exp) => (
              <tr key={exp.id}>
                <td className={TD}>{exp.name}</td>
                <td className={`${TD} text-right tabular-nums`}>
                  {formatWon(exp.amount)}
                  <DeltaBadge
                    value={
                      exp.amount -
                      (savedResult.expenses.find((e) => e.id === exp.id)
                        ?.amount ?? exp.amount)
                    }
                  />
                </td>
                <td className={TD}>
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                    {data.expenses.find((e) => e.id === exp.id)?.baseLabel} ×
                    <PercentInput
                      rate={exp.rate}
                      onChange={(r) => onChangeExpenseRate(exp.id, r)}
                      dirty={dirty.expense(exp.id)}
                      baseline={STANDARD_EXPENSE_RATES.get(exp.id)}
                    />
                  </span>
                </td>
                <td className={TD} />
              </tr>
            ))}

            {/* 경비 소계 */}
            <tr className="bg-slate-50 font-semibold text-primary">
              <td className={`${TD} font-semibold text-primary`}>[소계]</td>
              <td
                className={`${TD} text-right font-semibold tabular-nums text-primary`}
              >
                {formatWon(result.expenseSubtotal)}
                <DeltaBadge
                  value={result.expenseSubtotal - savedResult.expenseSubtotal}
                />
              </td>
              <td className={`${TD} font-semibold text-primary`}>
                {compositionPct(result.expenseSubtotal, result.netCost)}
              </td>
              <td className={TD} />
            </tr>

            {/* 계 (순공사원가) */}
            <tr className="bg-slate-50 font-semibold text-primary">
              <td className={`${TD} font-semibold text-primary`}>계</td>
              <td
                className={`${TD} text-right font-semibold tabular-nums text-primary`}
              >
                {formatWon(result.netCost)}
                <DeltaBadge value={result.netCost - savedResult.netCost} />
              </td>
              <td className={TD} />
              <td className={TD} />
            </tr>

            {/* 일반관리비 = 계 × rate */}
            <tr>
              <td className={TD}>일반관리비</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.generalAdmin)}
                <DeltaBadge
                  value={result.generalAdmin - savedResult.generalAdmin}
                />
              </td>
              <td className={TD}>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  계 ×
                  <PercentInput
                    rate={data.generalAdminRate}
                    onChange={onChangeGeneralAdminRate}
                    dirty={dirty.generalAdmin}
                    baseline={STANDARD_RATES.generalAdmin}
                  />
                </span>
              </td>
              <td className={TD} />
            </tr>

            {/* 이윤 = (노무+경비+일반관리비) × rate */}
            <tr>
              <td className={TD}>이윤</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.profit)}
                <DeltaBadge value={result.profit - savedResult.profit} />
              </td>
              <td className={TD}>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  노무+경비+일관 ×
                  <PercentInput
                    rate={data.profitRate}
                    onChange={onChangeProfitRate}
                    dirty={dirty.profit}
                    baseline={STANDARD_RATES.profit}
                  />
                </span>
              </td>
              <td className={TD} />
            </tr>

            {/* 폐기물처리비 (합계제외) */}
            <tr>
              <td className={TD}>폐기물처리비</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.wasteDisposal)}
              </td>
              <td className={TD} />
              <td className={`${TD} text-xs text-slate-400`}>합계제외</td>
            </tr>

            {/* 공급가액 = 계 + 일반관리비 + 이윤 */}
            <tr className="bg-slate-50 font-semibold text-primary">
              <td className={`${TD} font-semibold text-primary`}>공급가액</td>
              <td
                className={`${TD} text-right font-semibold tabular-nums text-primary`}
              >
                {formatWon(result.supplyPrice)}
                <DeltaBadge
                  value={result.supplyPrice - savedResult.supplyPrice}
                />
              </td>
              <td className={TD} />
              <td className={TD} />
            </tr>

            {/* 부가가치세 = 공급가액 × rate */}
            <tr>
              <td className={TD}>부가가치세</td>
              <td className={`${TD} text-right tabular-nums`}>
                {formatWon(result.vat)}
                <DeltaBadge value={result.vat - savedResult.vat} />
              </td>
              <td className={TD}>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  공급가액 ×
                  <PercentInput
                    rate={data.vatRate}
                    onChange={onChangeVatRate}
                    dirty={dirty.vat}
                    baseline={STANDARD_RATES.vat}
                  />
                </span>
              </td>
              <td className={TD} />
            </tr>

            {/* 도급액 / 총공사비 */}
            <tr className="bg-primary/10 font-semibold text-primary">
              <td className={`${TD} font-semibold text-primary`}>
                도급액/총공사비
              </td>
              <td
                className={`${TD} text-right font-semibold tabular-nums text-primary`}
              >
                {formatWon(result.contractPrice)}
                <DeltaBadge
                  value={result.contractPrice - savedResult.contractPrice}
                />
              </td>
              <td className={TD} />
              <td
                className={`${TD} text-right font-semibold tabular-nums text-primary`}
              >
                {formatWon(result.totalCost)}
                <DeltaBadge value={result.totalCost - savedResult.totalCost} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
